"use client"

import * as React from "react"
import { type ColumnDef } from "@tanstack/react-table"
import { useLocation, useNavigate } from "react-router-dom"
import {
  BanIcon,
  CheckIcon,
  CircleDashedIcon,
  CircleCheckIcon,
  FlagIcon,
  PaperclipIcon,
  PencilIcon,
  RefreshCwIcon,
  ScanSearchIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react"
import { toast } from "sonner"

import {
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  DataTable,
  ConfirmActionDialog,
  DetailPageHeader,
  EmptyState,
  HelperMessage,
  MobileScreenHeader,
  cn,
  valueLinkClass,
} from "@ibada/ui"
import { isZeroMoney } from "@ibada/shared-types/finance"
import { dbDateToDateOnly } from "@ibada/shared-types"

import { useTranslations } from "@/i18n"
import { LoadingRegion, SkeletonRowsCard, SkeletonStatCard } from "@/shared/components/skeletons"
import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { InvestigateButton, useInvestigate } from "@/features/finance/components/audit/investigate-button"
import { ReconciliationActionStrip } from "@/features/finance/components/reconciliation/reconciliation-action-strip"
import { ReconciliationStatusBadge } from "@/features/finance/components/reconciliation/reconciliation-status-badge"
import {
  ReconciliationLinePendingContext,
  ReconciliationLineStateCell,
} from "@/features/finance/components/reconciliation/reconciliation-line-state-cell"
import { ReconciliationOpeningCheckPanel } from "@/features/finance/components/reconciliation/reconciliation-opening-check-panel"
import { ReconciliationTimeline } from "@/features/finance/components/reconciliation/reconciliation-timeline"
import { ReconciliationTransitionDialog } from "@/features/finance/components/reconciliation/reconciliation-transition-dialog"
import {
  RECONCILIATION_DETAIL_TABS,
  ReconciliationDetailTab,
  buildReconciliationDetailTabHref,
  parseReconciliationDetailTabFromPathname,
} from "@/features/finance/model/reconciliation-detail-tab"
import { AdminRouteTabs } from "@/shared/components/admin-route-tabs"
import {
  TransactionMetadataEditDialog,
  type EditableTransactionMetadata,
} from "@/features/finance/components/transactions/transaction-metadata-edit-dialog"
import { formatMoney } from "@/features/finance/lib/finance-utils"
import { useBranchCurrency } from "@/features/finance/hooks/use-branch-currency"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import {
  ReconciliationLineState,
  ReconciliationSessionStatus,
} from "@/features/finance/model/finance-close-trust-types"
import type {
  ReconciliationLine,
  ReconciliationSessionDetail,
} from "@/features/finance/model/finance-close-trust-types"
import {
  useDeleteReconciliationProofMutation,
  useDeleteReconciliationSessionMutation,
  useReconciliationSessionQuery,
  useResyncReconciliationSessionMutation,
  useSetReconciliationLineMutation,
  useUploadReconciliationProofMutation,
} from "@/features/finance/services/finance-close-trust-query"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { useMobileBack } from "@/features/dashboard/hooks/use-mobile-screen"
import { MobileHeaderIconButton } from "@/shared/components/mobile-header-icon-button"
import { MobileSegmentedLinks } from "@/shared/components/mobile-list-chrome"
import { MobileFormRow, MobileFormSection, useFormatMoneyText } from "@/shared/components/mobile-form"
import { RowActionsMenu, type RowActionsMenuItem } from "@/shared/components/row-actions-menu"
import { DataTableMobileListVariant } from "@ibada/ui/components/data-table"
import { MobilePageSkeleton } from "@/shared/components/mobile-page-skeleton"

const displayDateOnly = (value: string) => dbDateToDateOnly(value) ?? value.slice(0, 10)

/**
 * One reconciliation session: the figures, the period's transactions, and whatever the viewer may do next.
 *
 * This is a ROUTED page rather than a selection inside a list, because a reviewer arrives here from a
 * notification. A session with no URL of its own cannot be linked to, which is the whole reason the previous
 * `useState` master/detail could not carry an approval workflow.
 */
export function ReconciliationSessionContent({ sessionId }: { sessionId: string }) {
  const location = useLocation()
  const activeTab = parseReconciliationDetailTabFromPathname(location.pathname)
  const t = useTranslations("finance.reconciliation")
  const tTransactions = useTranslations("finance.transactions")
  const toastError = useApiErrorToast("finance")
  const navigate = useNavigate()
  const { workspaceId, canEditFinance } = useFinancePageState()
  const branchCurrency = useBranchCurrency()
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug() ?? undefined
  const isMobile = useIsMobile()
  const tMobileNav = useTranslations("churchNav.mobile")
  const goBack = useMobileBack({ fallback: "/finance/reconciliation" })

  const sessionQuery = useReconciliationSessionQuery(workspaceId ? { workspaceId, sessionId } : null)
  const setLine = useSetReconciliationLineMutation()
  const resync = useResyncReconciliationSessionMutation()
  const discard = useDeleteReconciliationSessionMutation()
  const uploadProof = useUploadReconciliationProofMutation()
  const deleteProof = useDeleteReconciliationProofMutation()
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  const [confirmDiscard, setConfirmDiscard] = React.useState(false)
  const [editingTransaction, setEditingTransaction] = React.useState<EditableTransactionMetadata | null>(null)

  const session = sessionQuery.data
  const listUrl = buildBranchAdminUrl(branchSlug ?? "", "/finance/reconciliation", workspaceSlug)
  // NEVER "": `formatMoney` → Intl.NumberFormat({ currency: "" }) throws RangeError, and `currency` is null
  // for any code outside the Currency enum while `branch.baseCurrency` is an unconstrained VarChar(3). The
  // branch currency is the same fallback the list page uses.
  const currency = session?.currency ?? branchCurrency
  // `session.isEditable` is the server's lifecycle/separation-of-duties decision. It is necessary but not
  // sufficient for UI visibility: this page must also honour the central active-context finance decision.
  const isEditable = Boolean(session?.isEditable && canEditFinance)

  /*
   * Everything the row handler closes over lives in a ref, so the handler itself NEVER changes identity.
   *
   * `session` is `sessionQuery.data` — a new object on every refetch — and the mutation object is rebuilt each
   * render. A handler that depended on either rebuilt `lineColumns`, which gave every `cell` a new function
   * identity; TanStack renders `cell` as a COMPONENT TYPE, so a new identity remounts the whole cell subtree,
   * Radix's `composeRefs` detaches and reattaches, and its setState during detach re-enters the same render.
   * That is the "Maximum update depth exceeded" this screen was throwing on every tick of a checkbox.
   */
  const sessionRef = React.useRef(session)
  sessionRef.current = session
  const canEditRef = React.useRef(isEditable)
  canEditRef.current = isEditable
  const mutateLineRef = React.useRef(setLine.mutateAsync)
  mutateLineRef.current = setLine.mutateAsync
  const toastErrorRef = React.useRef(toastError)
  toastErrorRef.current = toastError
  // Re-entrancy guard, replacing the `disabled={setLine.isPending}` that used to sit in the deps. It is also
  // strictly better: it blocks a second click in the same tick, before React has re-rendered anything.
  const lineWriteInFlight = React.useRef(false)

  const applyLineState = React.useCallback(
    async (lineId: string, state: ReconciliationLineState, note?: string) => {
      const current = sessionRef.current
      if (!current || !canEditRef.current || lineWriteInFlight.current) return
      lineWriteInFlight.current = true
      try {
        await mutateLineRef.current({ workspaceId: current.workspaceId, sessionId: current.id, lineId, state, note })
      } catch (error) {
        toastErrorRef.current(error)
      } finally {
        lineWriteInFlight.current = false
      }
    },
    [],
  )

  // Declared ABOVE the loading return so hook order never depends on whether the session has arrived.
  const lineColumns = React.useMemo<ColumnDef<ReconciliationLine>[]>(
    () => [
      {
        id: "cleared",
        header: () => <span className='sr-only'>{t("session.cleared")}</span>,
        meta: { label: t("session.cleared") },
        enableSorting: false,
        size: 44,
        cell: ({ row }) => (
          <Checkbox
            // Names the ROW, not the column: on the one screen whose whole job is per-transaction ticking,
            // 25 checkboxes all announcing "Cleared" tells a screen-reader user nothing about which one.
            aria-label={`${t("session.cleared")}: ${
              row.original.transaction.description?.trim() ||
              row.original.transaction.transactionReference ||
              row.original.transaction.id.slice(0, 8)
            }`}
            checked={row.original.state === ReconciliationLineState.Cleared}
            disabled={!isEditable}
            onCheckedChange={(checked) =>
              void applyLineState(
                row.original.id,
                checked === true ? ReconciliationLineState.Cleared : ReconciliationLineState.Outstanding,
              )
            }
          />
        ),
      },
      {
        id: "description",
        accessorFn: (line) =>
          line.transaction.description?.trim() ||
          line.transaction.transactionReference ||
          line.transaction.id.slice(0, 8),
        header: tTransactions("table.columns.description"),
        cell: ({ row, getValue }) => (
          <span className='flex min-w-0 flex-col'>
            <span className='truncate'>{String(getValue())}</span>
            {row.original.transaction.description?.trim() && row.original.transaction.transactionReference ? (
              <span className='text-muted-foreground truncate text-xs'>
                {row.original.transaction.transactionReference}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: "occurredAt",
        accessorFn: (line) => line.transaction.occurredAt,
        header: tTransactions("table.columns.date"),
        cell: ({ getValue }) => <span className='tabular-nums'>{displayDateOnly(String(getValue()))}</span>,
      },
      {
        id: "amount",
        // Sorted as a NUMBER: these arrive as canonical decimal strings (IBA-116), and sorting those
        // lexically puts "9.00" above "10.00" — on a reconciliation that reads as missing money.
        accessorFn: (line) => Number(line.transaction.amount),
        header: tTransactions("table.columns.amount"),
        meta: { align: "right" },
        cell: ({ row }) => (
          <span className='font-mono tabular-nums'>
            {formatMoney(row.original.transaction.amount, currency)}
          </span>
        ),
      },
      {
        id: "state",
        accessorFn: (line) => line.state,
        header: t("session.columns.state"),
        enableSorting: false,
        cell: ({ row }) => (
          <ReconciliationLineStateCell line={row.original} editable={isEditable} onChange={applyLineState} />
        ),
      },
      {
        id: "actions",
        header: () => <span className='sr-only'>{tTransactions("table.columns.actions")}</span>,
        meta: { label: tTransactions("table.columns.actions"), align: "right" },
        enableSorting: false,
        size: 88,
        cell: ({ row }) =>
          isMobile ? (
            <ReconciliationLineMobileActions
              line={row.original}
              currency={currency}
              editable={isEditable}
              onChange={applyLineState}
              onEdit={setEditingTransaction}
            />
          ) : (
          <span className='flex items-center justify-end'>
            {isEditable ? (
              <Button
                variant='ghost'
                size='icon-sm'
                aria-label={tTransactions("edit.action")}
                title={tTransactions("edit.action")}
                onClick={() => setEditingTransaction(row.original.transaction)}
              >
                <PencilIcon className='size-3.5' />
              </Button>
            ) : null}
            <InvestigateButton context={{ transactionId: row.original.transaction.id }} iconOnly size='icon-sm' />
          </span>
          ),
      },
    ],
    // VALUES only. Anything here that changes identity per render remounts every cell — see above.
    [applyLineState, currency, isEditable, isMobile, t, tTransactions],
  )

  if (sessionQuery.isError) {
    // Reviewers arrive here from a notification link. A lost branch grant or a discarded session settles
    // instantly (401/403 are not retried), so without this the page sat on "Loading…" forever with no error,
    // no retry and no way back.
    return (
      <EmptyState
        tone='danger'
        title={t("session.error.title")}
        description={t("session.error.description")}
        action={
          <Button size='sm' variant='outline' onClick={() => navigate(listUrl)}>
            {t("session.back")}
          </Button>
        }
      />
    )
  }

  if (!session) {
    return isMobile ? (
      <MobilePageSkeleton hero groups={[4]} />
    ) : (
      // The session's figures row, then its lines.
      <LoadingRegion label={t("loading")} className='flex flex-col gap-4'>
        <div className='grid gap-3 sm:grid-cols-3'>
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </div>
        <SkeletonRowsCard rows={6} leading={false} />
      </LoadingRegion>
    )
  }

  // A reconciliation exists to prove two figures are the SAME figure, so zero is exact on minor units — it is
  // the only tolerance a reconciliation is allowed to have.
  const balanced = isZeroMoney(session.difference, currency)
  const clearedCount = session.lines.filter((line) => line.state === ReconciliationLineState.Cleared).length
  const outstandingCount = session.lines.length - clearedCount
  const lockedNotice =
    session.status === ReconciliationSessionStatus.PendingReview
      ? ({ key: "session.locked.pendingReview", tone: "info" } as const)
      : session.status === ReconciliationSessionStatus.Approved
        ? ({ key: "session.locked.approved", tone: "success" } as const)
        : session.status === ReconciliationSessionStatus.Rejected
          ? // Warning, not info: this one is not a status update, it is work handed back.
            ({ key: "session.locked.rejected", tone: "warning" } as const)
          : null
  // An empty period reads as 0% rather than NaN — no lines is not "fully cleared".
  const clearedPercent = session.lines.length > 0 ? Math.round((clearedCount / session.lines.length) * 100) : 0

  const handleResync = async () => {
    if (!canEditRef.current) return
    try {
      await resync.mutateAsync({ workspaceId: session.workspaceId, sessionId: session.id })
      toast.success(t("session.resynced"))
    } catch (error) {
      toastError(error)
    }
  }

  const handleUploadProof = async (file: File) => {
    if (!canEditRef.current) return
    try {
      await uploadProof.mutateAsync({ workspaceId: session.workspaceId, sessionId: session.id, file })
      toast.success(t("session.proof.uploaded"))
    } catch (error) {
      toastError(error)
    }
  }

  const handleDeleteProof = async (fileId: string) => {
    if (!canEditRef.current) return
    try {
      await deleteProof.mutateAsync({ workspaceId: session.workspaceId, sessionId: session.id, fileId })
      toast.success(t("session.proof.removed"))
    } catch (error) {
      toastError(error)
    }
  }

  const handleDiscard = async () => {
    if (!canEditRef.current) return
    try {
      await discard.mutateAsync({ workspaceId: session.workspaceId, sessionId: session.id })
      toast.success(t("session.discarded"))
      setConfirmDiscard(false)
      navigate(listUrl)
    } catch (error) {
      toastError(error)
      setConfirmDiscard(false)
    }
  }

  const differenceTone = balanced ? "text-emerald-500" : "text-amber-500"
  const isTransactionsTab = activeTab === ReconciliationDetailTab.Transactions

  const proofInput = (
    <input
      ref={fileInputRef}
      type='file'
      className='hidden'
      onChange={(event) => {
        const file = event.target.files?.[0]
        if (file) void handleUploadProof(file)
        event.target.value = ""
      }}
    />
  )

  const editDialogs = (
    <>
      {editingTransaction ? (
        <TransactionMetadataEditDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditingTransaction(null)
          }}
          transaction={editingTransaction}
        />
      ) : null}

      <ConfirmActionDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title={t("dialogs.discard.title")}
        description={t("dialogs.discard.body")}
        confirmLabel={t("dialogs.discard.confirm")}
        cancelLabel={t("dialogs.cancel")}
        confirmVariant='destructive'
        pending={discard.isPending}
        onConfirm={() => void handleDiscard()}
      />
    </>
  )

  if (isMobile) {
    // Phone: the shared screen header (back to the list, refresh as an icon), the account as the title with the status
    // and period under it, the difference as the hero, the two balances as rows, the decision as pills, then the
    // three tabs as a segmented control (each still a real path). Discarding is a red row at the very end.
    return (
      <div className='flex flex-col gap-3.5'>
        <MobileScreenHeader
          title={session.financeAccount.name}
          subtitle={
            <span className='flex min-w-0 items-center gap-2'>
              <span className='shrink-0'>
                <ReconciliationStatusBadge status={session.status} />
              </span>
              <span className='tabular-nums'>
                {displayDateOnly(session.statementDateFrom)} → {displayDateOnly(session.statementDateTo)}
              </span>
            </span>
          }
          // A review notification lands here directly, so back from a cold open goes to the list, not Finance.
          back={{ label: tMobileNav("back"), onBack: goBack }}
          actions={
            isEditable ? (
              <MobileHeaderIconButton
                icon={RefreshCwIcon}
                label={t("session.refresh")}
                loading={resync.isPending}
                onClick={() => void handleResync()}
              />
            ) : undefined
          }
        />

        <div className='flex flex-col gap-0.5 rounded-[1.25rem] bg-card px-4 py-3.5'>
          <span className='text-[13px] font-semibold text-muted-foreground'>{t("session.difference")}</span>
          <span
            className={cn(
              "truncate text-[1.75rem] leading-tight font-extrabold tabular-nums",
              balanced ? "text-primary" : "text-amber-600 dark:text-amber-400",
            )}
          >
            {formatMoney(session.difference, currency)}
          </span>
          <span className='text-[13px] font-semibold text-muted-foreground'>
            {balanced
              ? t("session.balanced")
              : t("session.equation", {
                  statement: formatMoney(session.statementBalance, currency),
                  system: formatMoney(session.systemBalance, currency),
                })}
          </span>
        </div>

        <MobileFormSection>
          <MobileFormRow label={t("session.statementBalance")} value={<span className='tabular-nums'>{formatMoney(session.statementBalance, currency)}</span>} />
          <MobileFormRow label={t("session.systemBalance")} value={<span className='tabular-nums'>{formatMoney(session.systemBalance, currency)}</span>} />
        </MobileFormSection>

        <ReconciliationActionStrip session={session} canEdit={isEditable} />

        {session.openingCheck ? <ReconciliationOpeningCheckPanel check={session.openingCheck} currency={currency} /> : null}

        {lockedNotice ? (
          <HelperMessage tone={lockedNotice.tone} size='sm'>
            {t(lockedNotice.key)}
          </HelperMessage>
        ) : null}

        <MobileSegmentedLinks
          value={activeTab}
          items={RECONCILIATION_DETAIL_TABS.map((tab) => ({
            value: tab,
            label: t(`tabs.${tab}` as never),
            href: buildReconciliationDetailTabHref(branchSlug ?? "", session.id, tab, workspaceSlug),
          }))}
        />

        {isTransactionsTab ? (
          <>
            <div className='flex flex-col gap-2 rounded-2xl bg-card px-4 py-3'>
              <div className='flex items-center justify-between gap-3'>
                <span className='truncate text-sm font-extrabold tabular-nums'>
                  {t("session.clearedCount", { cleared: clearedCount, total: session.lines.length })}
                </span>
                {outstandingCount > 0 ? (
                  <span className='shrink-0 text-xs font-semibold text-muted-foreground tabular-nums'>
                    {t("session.outstandingCount", { count: outstandingCount })}
                  </span>
                ) : null}
              </div>
              <div className='h-1.5 w-full overflow-hidden rounded-full bg-muted'>
                <div
                  className='h-full rounded-full bg-primary transition-[width]'
                  style={{ width: `${clearedPercent}%` }}
                  role='progressbar'
                  aria-valuenow={clearedCount}
                  aria-valuemin={0}
                  aria-valuemax={session.lines.length}
                  aria-label={t("session.cleared")}
                />
              </div>
              {session.isEditable && session.lines.length > 0 && clearedCount === 0 ? (
                <span className='text-xs font-semibold text-muted-foreground'>{t("session.differenceHint")}</span>
              ) : null}
            </div>

            <ReconciliationLinePendingContext.Provider value={setLine.isPending}>
              <DataTable
                data={session.lines}
                columns={lineColumns}
                paginationRole='local'
                initialPagination={{ pageIndex: 0, pageSize: 25 }}
                emptyMessage={t("session.empty.title")}
                mobileListVariant={DataTableMobileListVariant.Grouped}
                renderMobileRow={(row) => (
                  <ReconciliationLineMobileRow
                    line={row.original}
                    currency={currency}
                    editable={isEditable}
                    onChange={applyLineState}
                  />
                )}
              />
            </ReconciliationLinePendingContext.Provider>
          </>
        ) : null}

        {activeTab === ReconciliationDetailTab.Statement ? (
          <>
            <MobileFormSection footer={t("session.proof.hint")}>
              {session.proofFiles.length === 0 ? (
                <div className='flex min-h-[3.25rem] items-center px-4 text-[15px] font-semibold text-muted-foreground'>
                  {t("mobile.noProof")}
                </div>
              ) : (
                session.proofFiles.map((file) => (
                  <div key={file.id} className='flex min-h-[3.25rem] min-w-0 items-center gap-3 px-4'>
                    <PaperclipIcon aria-hidden className='size-4 shrink-0 text-muted-foreground' />
                    <a
                      href={file.url}
                      target='_blank'
                      rel='noreferrer'
                      className='min-w-0 flex-1 truncate text-base font-semibold text-primary'
                    >
                      {file.fileName}
                    </a>
                    {isEditable ? (
                      <button
                        type='button'
                        aria-label={t("session.proof.remove")}
                        disabled={deleteProof.isPending}
                        onClick={() => void handleDeleteProof(file.id)}
                        className='inline-flex size-9 shrink-0 items-center justify-center rounded-full text-destructive active:bg-destructive/10 disabled:opacity-45'
                      >
                        <XIcon aria-hidden className='size-4' />
                      </button>
                    ) : null}
                  </div>
                ))
              )}
            </MobileFormSection>
            {isEditable ? (
              <>
                {proofInput}
                <button
                  type='button'
                  disabled={uploadProof.isPending}
                  onClick={() => fileInputRef.current?.click()}
                  className='flex h-[3.25rem] w-full items-center justify-center gap-2 rounded-full bg-card text-base font-extrabold text-primary ring-1 ring-border/70 active:bg-muted disabled:opacity-45'
                >
                  <PaperclipIcon aria-hidden className='size-4' />
                  {t("session.proof.attach")}
                </button>
              </>
            ) : null}
          </>
        ) : null}

        {activeTab === ReconciliationDetailTab.History ? (
          <div className='rounded-2xl bg-card px-4 py-4'>
            <ReconciliationTimeline events={session.timeline} />
          </div>
        ) : null}

        {/* Destructive and rare, so it is a labelled red row at the end, not an icon in the top bar. */}
        {isEditable ? (
          <MobileFormSection>
            <button
              type='button'
              onClick={() => setConfirmDiscard(true)}
              className='flex min-h-[3.25rem] w-full items-center justify-center gap-2 px-4 text-base font-extrabold text-destructive active:bg-destructive/10'
            >
              <Trash2Icon aria-hidden className='size-4' />
              {t("session.discard")}
            </button>
          </MobileFormSection>
        ) : null}

        {editDialogs}
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {/* Status and the decision live WITH the title, not on a row of their own — the first thing a reviewer
          needs is "what is this and what can I do about it", in one glance. */}
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <DetailPageHeader
          onBack={() => navigate(listUrl)}
          backLabel={t("session.back")}
          backHistoryFirst={false}
          title={session.financeAccount.name}
          description={`${displayDateOnly(session.statementDateFrom)} → ${displayDateOnly(session.statementDateTo)}`}
        />
        <div className='flex shrink-0 flex-wrap items-center gap-2'>
          <ReconciliationStatusBadge status={session.status} />
          <ReconciliationActionStrip session={session} canEdit={isEditable} />
        </div>
      </div>

      {/* The difference IS the page. The two balances that produce it are stated beneath as the equation they
          are, rather than as three equal boxes that make the reader work out which number is the answer. */}
      <Card>
        <CardContent className='flex flex-wrap items-end justify-between gap-4 py-4'>
          <div className='min-w-0'>
            <p className='text-muted-foreground text-xs font-medium'>{t("session.difference")}</p>
            <p className={cn("font-mono text-3xl font-semibold tabular-nums", differenceTone)}>
              {formatMoney(session.difference, currency)}
            </p>
            <p className='text-muted-foreground mt-1 text-xs'>
              {balanced
                ? t("session.balanced")
                : t("session.equation", {
                    statement: formatMoney(session.statementBalance, currency),
                    system: formatMoney(session.systemBalance, currency),
                  })}
            </p>
          </div>

          <dl className='flex gap-6'>
            <SummaryFigure
              label={t("session.statementBalance")}
              value={formatMoney(session.statementBalance, currency)}
            />
            <SummaryFigure
              label={t("session.systemBalance")}
              value={formatMoney(session.systemBalance, currency)}
            />
          </dl>
        </CardContent>
      </Card>

      {/* Three different states are read-only, and they mean opposite things: waiting on a reviewer, signed
          off, or sent back. One shared "being reviewed" line told a preparer their REJECTED reconciliation
          was still in the queue — the exact opposite of the action they need to take. */}
      {session.openingCheck ? (
        <ReconciliationOpeningCheckPanel check={session.openingCheck} currency={currency} />
      ) : null}

      {lockedNotice ? (
        <HelperMessage tone={lockedNotice.tone} size='sm'>
          {t(lockedNotice.key)}
        </HelperMessage>
      ) : null}

      {/* Each tab is a real URL segment, so a reviewer can be linked straight to the history rather than
          told to click twice once they arrive. */}
      <AdminRouteTabs
        value={activeTab}
        className=''
        items={RECONCILIATION_DETAIL_TABS.map((tab) => ({
          value: tab,
          label: t(`tabs.${tab}` as never),
          href: buildReconciliationDetailTabHref(branchSlug ?? '', session.id, tab, workspaceSlug),
        }))}
      />

      {/* One toolbar. Buttons carry their own meaning (icon + verb) instead of a sentence of prose beside
          them; the longer explanation is a title attribute, where it costs nothing until it is wanted. */}
      {isTransactionsTab && isEditable ? (
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            size='sm'
            variant='outline'
            onClick={() => void handleResync()}
            disabled={!isEditable || resync.isPending}
            title={t("session.refreshHint")}
          >
            <RefreshCwIcon className={cn("size-3.5", resync.isPending && "animate-spin")} />
            {t("session.refresh")}
          </Button>

          <Button
            size='sm'
            variant='ghost'
            className='text-destructive hover:text-destructive ml-auto'
            disabled={!isEditable}
            onClick={() => setConfirmDiscard(true)}
          >
            {t("session.discard")}
          </Button>
        </div>
      ) : null}

      {activeTab === ReconciliationDetailTab.Statement ? (
        <Card>
          <CardHeader className={session.proofFiles.length > 0 ? "border-b" : undefined}>
            <CardTitle>{t("tabs.statement")}</CardTitle>
            <CardDescription>{t("session.proof.hint")}</CardDescription>
            {isEditable ? (
              <CardAction>
                {proofInput}
                <Button
                  size='sm'
                  variant='outline'
                  disabled={!isEditable || uploadProof.isPending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <PaperclipIcon className='size-3.5' />
                  {t("session.proof.attach")}
                </Button>
              </CardAction>
            ) : null}
          </CardHeader>
          {session.proofFiles.length === 0 ? null : (
            <CardContent className='flex flex-wrap gap-2'>
          {session.proofFiles.map((file) => (
            <span
              key={file.id}
              className='bg-muted/50 inline-flex max-w-full items-center gap-1.5 rounded-md border px-2 py-1'
            >
              <PaperclipIcon className='text-muted-foreground size-3.5 shrink-0' />
              <a href={file.url} target='_blank' rel='noreferrer' className={cn("truncate text-xs", valueLinkClass)}>
                {file.fileName}
              </a>
              {isEditable ? (
                <button
                  type='button'
                  aria-label={t("session.proof.remove")}
                  title={t("session.proof.remove")}
                  className='text-muted-foreground hover:text-destructive shrink-0'
                  disabled={!isEditable || deleteProof.isPending}
                  onClick={() => void handleDeleteProof(file.id)}
                >
                  <XIcon className='size-3.5' />
                </button>
              ) : null}
            </span>
          ))}
            </CardContent>
          )}
        </Card>
      ) : null}

      {isTransactionsTab ? (
        <div className='space-y-2'>
          {/* A status bar ABOVE the table rather than a card wrapped AROUND it — DataTable brings its own
              surface, toolbar and pagination, so boxing it would nest one panel inside another.

              It carries PROGRESS, not just a count: ticking lines is the work of this page, and how much is
              left is the question you ask on every tick. */}
          <Card size='sm'>
            <CardContent className='flex flex-wrap items-center gap-x-4 gap-y-2'>
              <div className='flex min-w-0 flex-1 items-center gap-3'>
                <span className='shrink-0 text-sm font-medium tabular-nums'>
                  {t("session.clearedCount", { cleared: clearedCount, total: session.lines.length })}
                </span>
                {/* A bar rather than a percentage: the shape of "nearly done" is read at a glance, and a
                    percentage of a handful of lines is false precision. */}
                <div className='bg-muted h-1.5 min-w-24 flex-1 overflow-hidden rounded-full'>
                  <div
                    className='bg-primary h-full rounded-full transition-[width]'
                    style={{ width: `${clearedPercent}%` }}
                    role='progressbar'
                    aria-valuenow={clearedCount}
                    aria-valuemin={0}
                    aria-valuemax={session.lines.length}
                    aria-label={t("session.cleared")}
                  />
                </div>
              </div>

              {outstandingCount > 0 ? (
                <span className='text-muted-foreground shrink-0 text-xs tabular-nums'>
                  {t("session.outstandingCount", { count: outstandingCount })}
                </span>
              ) : null}

              {session.isEditable && session.lines.length > 0 && clearedCount === 0 ? (
                <span className='text-muted-foreground basis-full text-xs'>{t("session.differenceHint")}</span>
              ) : null}
            </CardContent>
          </Card>

          <ReconciliationLinePendingContext.Provider value={setLine.isPending}>
            <DataTable
              data={session.lines}
              columns={lineColumns}
              paginationRole='local'
              initialPagination={{ pageIndex: 0, pageSize: 25 }}
              emptyMessage={t("session.empty.title")}
            />
          </ReconciliationLinePendingContext.Provider>
        </div>
      ) : null}

      {activeTab === ReconciliationDetailTab.History ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("timeline.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ReconciliationTimeline events={session.timeline} />
          </CardContent>
        </Card>
      ) : null}

      {editDialogs}
    </div>
  )
}

type LineStateChange = (lineId: string, state: ReconciliationLineState, note?: string) => void | Promise<void>

/** What a line is, for its phone row and its actions sheet: the description, or its reference, or its id. */
function lineTitle(line: ReconciliationLine): string {
  return line.transaction.description?.trim() || line.transaction.transactionReference || line.transaction.id.slice(0, 8)
}

const LINE_STATE_PILL: Record<ReconciliationLineState, string> = {
  [ReconciliationLineState.Outstanding]: "bg-muted text-muted-foreground",
  [ReconciliationLineState.Cleared]: "bg-primary/12 text-primary",
  [ReconciliationLineState.Excluded]: "bg-muted text-foreground/70",
  [ReconciliationLineState.Investigating]: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
}

/**
 * One period transaction on a phone. The round check on the left clears it with one tap (the work of this
 * page); the date, its verdict and the verdict's reason sit under the description, the amount on the right.
 */
function ReconciliationLineMobileRow({
  line,
  currency,
  editable,
  onChange,
  inSheet = false,
}: {
  line: ReconciliationLine
  currency: string
  editable: boolean
  onChange: LineStateChange
  /** Heading the actions sheet: no check button there. */
  inSheet?: boolean
}) {
  const t = useTranslations("finance.reconciliation")
  const pending = React.useContext(ReconciliationLinePendingContext)
  const formatMoneyText = useFormatMoneyText(currency)
  const cleared = line.state === ReconciliationLineState.Cleared
  const showState = line.state !== ReconciliationLineState.Outstanding && line.state !== ReconciliationLineState.Cleared
  const title = lineTitle(line)
  return (
    <div className='flex min-w-0 flex-1 items-center gap-3'>
      {inSheet ? null : (
        <button
          type='button'
          role='checkbox'
          aria-checked={cleared}
          aria-label={`${t("session.cleared")}: ${title}`}
          disabled={!editable || pending}
          onClick={() =>
            void onChange(line.id, cleared ? ReconciliationLineState.Outstanding : ReconciliationLineState.Cleared)
          }
          className={cn(
            "inline-flex size-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors disabled:opacity-60",
            cleared ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
          )}
        >
          {cleared ? <CheckIcon aria-hidden className='size-4' strokeWidth={3} /> : null}
        </button>
      )}
      <span className='flex min-w-0 flex-1 flex-col gap-1'>
        <span className='truncate text-[15px] leading-tight font-extrabold text-foreground'>{title}</span>
        <span className='flex min-w-0 items-center gap-1.5 text-xs font-semibold text-muted-foreground'>
          <span className='shrink-0 tabular-nums'>{displayDateOnly(line.transaction.occurredAt)}</span>
          {showState ? (
            <span className={cn("inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[11px] font-extrabold", LINE_STATE_PILL[line.state])}>
              {t(`lineState.${line.state}` as never)}
            </span>
          ) : null}
          {line.stateNote ? <span className='min-w-0 truncate'>{line.stateNote}</span> : null}
        </span>
      </span>
      <span
        className={cn(
          "max-w-[40%] shrink-0 truncate text-right text-[15px] font-extrabold tabular-nums",
          line.state === ReconciliationLineState.Excluded ? "text-muted-foreground line-through" : "text-foreground",
        )}
      >
        {formatMoneyText(line.transaction.amount)}
      </span>
    </div>
  )
}

/**
 * A line's ⋯ on a phone: its verdicts (cleared, outstanding, and the two that need a written reason) and the
 * transaction itself (edit, investigate), in the shared bottom sheet. The reason is asked in a sheet of its own
 * once this one has closed.
 */
function ReconciliationLineMobileActions({
  line,
  currency,
  editable,
  onChange,
  onEdit,
}: {
  line: ReconciliationLine
  currency: string
  editable: boolean
  onChange: LineStateChange
  onEdit: (transaction: EditableTransactionMetadata) => void
}) {
  const t = useTranslations("finance.reconciliation")
  const tTransactions = useTranslations("finance.transactions")
  const tAudit = useTranslations("finance.audit")
  const investigate = useInvestigate()
  const pending = React.useContext(ReconciliationLinePendingContext)
  const [verdict, setVerdict] = React.useState<ReconciliationLineState | null>(null)

  const verdicts: RowActionsMenuItem[] = editable
    ? [
        ...(line.state !== ReconciliationLineState.Cleared
          ? [{ id: "cleared", label: t("mobile.markCleared"), icon: CircleCheckIcon, disabled: pending, onSelect: () => void onChange(line.id, ReconciliationLineState.Cleared) }]
          : []),
        ...(line.state !== ReconciliationLineState.Outstanding
          ? [{ id: "outstanding", label: t("mobile.markOutstanding"), icon: CircleDashedIcon, disabled: pending, onSelect: () => void onChange(line.id, ReconciliationLineState.Outstanding) }]
          : []),
        ...(line.state !== ReconciliationLineState.Investigating
          ? [{ id: "investigating", label: t(`lineState.dialog.${ReconciliationLineState.Investigating}.confirm` as never), icon: FlagIcon, disabled: pending, onSelect: () => setVerdict(ReconciliationLineState.Investigating) }]
          : []),
        ...(line.state !== ReconciliationLineState.Excluded
          ? [{ id: "excluded", label: t(`lineState.dialog.${ReconciliationLineState.Excluded}.confirm` as never), icon: BanIcon, destructive: true, disabled: pending, onSelect: () => setVerdict(ReconciliationLineState.Excluded) }]
          : []),
      ]
    : []
  const transactionActions: RowActionsMenuItem[] = [
    ...(editable ? [{ id: "edit", label: tTransactions("edit.action"), icon: PencilIcon, onSelect: () => onEdit(line.transaction) }] : []),
    { id: "audit", label: tAudit("investigate"), icon: ScanSearchIcon, onSelect: () => investigate({ transactionId: line.transaction.id }) },
  ]

  return (
    <>
      <RowActionsMenu
        label={lineTitle(line)}
        openLabel={t("mobile.actions")}
        sheetHeader={<ReconciliationLineMobileRow line={line} currency={currency} editable={false} onChange={onChange} inSheet />}
        groups={[verdicts, transactionActions]}
      />
      {verdict ? (
        <ReconciliationTransitionDialog
          open
          onOpenChange={(open) => {
            if (!open) setVerdict(null)
          }}
          title={t(`lineState.dialog.${verdict}.title` as never)}
          description={t(`lineState.dialog.${verdict}.body` as never)}
          confirmLabel={t(`lineState.dialog.${verdict}.confirm` as never)}
          noteLabel={t("lineState.dialog.reason")}
          notePlaceholder={t(`lineState.dialog.${verdict}.placeholder` as never)}
          noteRequired
          destructive={verdict === ReconciliationLineState.Excluded}
          pending={pending}
          onConfirm={(note) => {
            void onChange(line.id, verdict, note)
            setVerdict(null)
          }}
        />
      ) : null}
    </>
  )
}

/** A supporting figure beside the headline difference — deliberately quieter than it. */
function SummaryFigure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className='text-muted-foreground text-xs'>{label}</dt>
      <dd className='font-mono text-sm font-medium tabular-nums'>{value}</dd>
    </div>
  )
}

export type { ReconciliationSessionDetail }
